// src/app/_util/tag-color.util.ts
// Colour helpers for tag editing (Tag Manager — private and Space tags alike).

export const COMMON_TAG_COLORS: ReadonlyArray<{ name: string; value: string }> = [
  { name: 'Slate', value: '#64748B' },
  { name: 'Gray', value: '#6B7280' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Amber', value: '#F59E0B' },
  { name: 'Yellow', value: '#EAB308' },
  { name: 'Lime', value: '#84CC16' },
  { name: 'Green', value: '#22C55E' },
  { name: 'Emerald', value: '#10B981' },
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Sky', value: '#0EA5E9' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Violet', value: '#8B5CF6' },
  { name: 'Purple', value: '#A855F7' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Rose', value: '#F43F5E' },
];

/** "#abc" / "abc" / "#AABBCC" → "#AABBCC"-style; empty for empty input. */
export function normalizeHexColor(value: string | null | undefined): string {
  if (!value) return '';
  const raw = String(value).replace(/^#/, '').trim();
  if (/^[0-9a-fA-F]{3}$/.test(raw)) return '#' + raw[0] + raw[0] + raw[1] + raw[1] + raw[2] + raw[2];
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return '#' + raw;
  return value.startsWith('#') ? value : '#' + value;
}

/** PrimeNG's colour picker can emit a string or an {r,g,b}/{hex}/{value} object. */
export function colorValueToString(value: string | object | null | undefined): string {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return '';
  const record = value as Record<string, unknown>;
  const hex = typeof record['hex'] === 'string' ? record['hex'] : typeof record['value'] === 'string' ? record['value'] : '';
  if (hex) return hex;
  const [r, g, b] = [Number(record['r']), Number(record['g']), Number(record['b'])];
  if ([r, g, b].every((c) => Number.isFinite(c) && c >= 0 && c <= 255)) {
    const toHex = (c: number) => Math.round(c).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  return '';
}

/** Dark or light text, whichever reads better on `backgroundColor`. */
export function readableTextColor(backgroundColor: string | null | undefined): string {
  const normalized = normalizeHexColor(backgroundColor);
  if (!/^#[0-9a-fA-F]{6}$/.test(normalized)) return '#0f172a';
  const hex = normalized.slice(1);
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62 ? '#0f172a' : '#ffffff';
}

export function slugifyTagName(name: string): string {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
